/**
 * 전자계약 서명 화면 — OZ eForm 대체.
 *
 * 두 경로에서 같은 화면이 열린다.
 *   1) 시민이 SMS 로 받은 링크 (일반 모바일 브라우저)
 *   2) 설치기사가 Flutter WebView 안에서 여는 현장 서명
 * 레거시는 이 둘이 JSP 서식과 앱 내장 PDF 생성기로 완전히 이중 구현되어 있었다.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiClient, ApiError, type ContractView } from '@solar/api-client';
import {
  PageRenderer,
  propagationTargets,
  requiredSlots,
  visiblePages,
  type SignatureResult,
} from '@solar/eform-core';
import { setBrightness } from '@solar/native-bridge';
import { navigate, useLocation, withQuery } from '@solar/ui';

const api = new ApiClient({ baseUrl: import.meta.env.VITE_API_BASE_URL ?? '' });

type Status =
  | { kind: 'loading' }
  | { kind: 'error'; message: string; returnUrl?: string | null }
  | { kind: 'ready'; view: ContractView }
  | { kind: 'done'; pdfName: string; returnUrl: string | null };

/** 시민 포털 '진행 조회' 로 돌아가는 주소 — from=sign 이면 포털이 바로 조회한다. */
function returnUrlOf(view: ContractView): string | null {
  const base = view.citizen_status_url;
  return base ? `${base}${base.includes('?') ? '&' : '?'}from=sign` : null;
}

/** 링크는 `/e/{token}` 형태다. */
function tokenFromLocation(): string | null {
  const match = /\/e\/([^/?#]+)/.exec(window.location.pathname);
  return match?.[1] ?? null;
}

export function App() {
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const [signatures, setSignatures] = useState<Record<string, SignatureResult | null>>({});
  const [agreements, setAgreements] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  const token = useMemo(tokenFromLocation, []);
  // 쪽 번호는 주소(?page=2)에 둔다 — 뒤로가기가 '이전' 과 같고, 새로고침해도 쪽이 유지된다.
  // 서명 자체는 상태에만 있으므로 새로고침하면 다시 해야 한다(토큰 링크는 그대로 유효).
  const location = useLocation();

  useEffect(() => {
    if (!token) {
      setStatus({ kind: 'error', message: '잘못된 링크입니다.' });
      return;
    }
    api
      .viewContract(token)
      .then((view) => {
        if (view.already_signed) {
          setStatus({
            kind: 'error',
            message: '이미 서명이 완료된 계약입니다.',
            returnUrl: returnUrlOf(view),
          });
          return;
        }
        setStatus({ kind: 'ready', view });
      })
      .catch((err: ApiError) => setStatus({ kind: 'error', message: err.message }));
  }, [token]);

  // 서명 화면에서는 화면을 밝게 한다 (야외 현장 서명 대응). 브라우저에서는 무시된다.
  useEffect(() => {
    if (status.kind !== 'ready') return;
    void setBrightness(1);
    return () => void setBrightness(0.5);
  }, [status.kind]);

  const view = status.kind === 'ready' ? status.view : null;
  const pages = useMemo(
    () => (view ? visiblePages(view.template.pages, view.data) : []),
    [view],
  );
  const required = useMemo(
    () => (view ? requiredSlots(view.template.pages, view.data) : []),
    [view],
  );
  const requestedPage = Number.parseInt(location.search.get('page') ?? '1', 10);
  const pageIndex = Math.min(
    Math.max(Number.isFinite(requestedPage) ? requestedPage - 1 : 0, 0),
    Math.max(pages.length - 1, 0),
  );
  const goPage = useCallback(
    (index: number) => navigate(withQuery(location, 'page', index <= 0 ? null : String(index + 1))),
    [location],
  );

  /**
   * 레거시 SignPad0 → SignPad1~9 복사 동작을 서식 정의(propagate)대로 재현한다.
   * 페이지마다 다시 서명하게 하면 실사용 이탈이 크다.
   */
  const handleSign = useCallback(
    (slot: string, result: SignatureResult | null) => {
      setSignatures((prev) => {
        const next = { ...prev, [slot]: result };
        if (view) {
          for (const target of propagationTargets(view.template.pages, slot)) {
            next[target] = result;
          }
        }
        return next;
      });
    },
    [view],
  );

  const missing = required.filter((slot) => !signatures[slot]);
  const rentalRequired = view ? isRentalHousing(view.data) : false;
  const rentalAgreed = agreements.agree_rental === true;
  const canSubmit = missing.length === 0 && (!rentalRequired || rentalAgreed) && !submitting;

  async function submit() {
    if (!token || !view) return;
    setSubmitting(true);
    try {
      const result = await api.signContract(token, {
        signatures: required.map((slot) => ({
          slot,
          image: signatures[slot]!.dataUrl,
          strokes: signatures[slot]!.strokes,
        })),
        agreements,
      });
      setStatus({ kind: 'done', pdfName: result.pdf_name, returnUrl: returnUrlOf(view) });
      // 완료 뒤 뒤로가기로 서명 쪽들을 되짚지 않도록 쪽 쿼리를 지운다.
      navigate(withQuery(location, 'page', null), { replace: true });
      window.scrollTo(0, 0);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : '제출에 실패했습니다.';
      setStatus({ kind: 'error', message });
    } finally {
      setSubmitting(false);
    }
  }

  if (status.kind === 'loading') return <Centered>불러오는 중…</Centered>;

  if (status.kind === 'error') {
    return (
      <Centered>
        <h1>계약서를 열 수 없습니다</h1>
        <p>{status.message}</p>
        <p className="muted">링크가 만료되었거나 이미 사용되었을 수 있습니다.</p>
        {status.returnUrl && <ReturnButton url={status.returnUrl} />}
      </Centered>
    );
  }

  if (status.kind === 'done') {
    return (
      <Centered>
        <h1>서명이 완료되었습니다</h1>
        <p>계약서가 발급되었습니다. 담당자가 확인 후 연락드립니다.</p>
        <p className="muted">{status.pdfName}</p>
        {status.returnUrl ? (
          <>
            <ReturnButton url={status.returnUrl} />
            <p className="muted">진행 상황 화면에서 계약 상태("서명 완료")를 확인하실 수 있습니다.</p>
          </>
        ) : (
          <p className="muted">이 창은 닫으셔도 됩니다.</p>
        )}
      </Centered>
    );
  }

  const page = pages[pageIndex];
  const isLast = pageIndex === pages.length - 1;

  return (
    <div className="eform">
      <header className="eform__head">
        <h1>{status.view.template.title}</h1>
        <span className="eform__progress">
          {pageIndex + 1} / {pages.length}
        </span>
      </header>

      {page && (
        <PageRenderer
          page={page}
          data={status.view.data}
          signatures={signatures}
          onSign={handleSign}
        />
      )}

      {isLast && rentalRequired && (
        <label className="agree">
          <input
            type="checkbox"
            checked={rentalAgreed}
            onChange={(e) => setAgreements((a) => ({ ...a, agree_rental: e.target.checked }))}
          />
          임대주택 설비 이전·철거·승계에 관한 사항에 동의합니다. (필수)
        </label>
      )}

      <footer className="eform__foot">
        <button type="button" onClick={() => goPage(pageIndex - 1)} disabled={pageIndex === 0}>
          이전
        </button>

        {isLast ? (
          <button type="button" className="primary" onClick={submit} disabled={!canSubmit}>
            {submitting ? '제출 중…' : '서명 제출'}
          </button>
        ) : (
          <button type="button" className="primary" onClick={() => goPage(pageIndex + 1)}>
            다음
          </button>
        )}
      </footer>

      {isLast && missing.length > 0 && (
        <p className="warn">서명이 남아 있습니다: {missing.length}곳</p>
      )}
    </div>
  );
}

/** 서버(APT_TYPES_REQUIRING_RENTAL_CONSENT)와 같은 규칙. */
function isRentalHousing(data: unknown): boolean {
  const aptType = (data as { order?: { apt_type?: number } }).order?.apt_type;
  return aptType === 2 || aptType === 7;
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="centered">{children}</div>;
}

/**
 * 시민 포털로 돌아가는 버튼. 서명 페이지는 시민 앱에서 **같은 탭**으로 열리므로
 * (apps/citizen contractLink.ts) 여기서 돌아가야 흐름이 닫힌다. 주소는 서버 설정
 * (CITIZEN_BASE_URL)에서 오고 쿼리로 받지 않는다 — 열린 리다이렉트를 막기 위해서다.
 */
function ReturnButton({ url }: { url: string }) {
  return (
    <button type="button" className="primary big" onClick={() => window.location.assign(url)}>
      진행 상황 확인하기
    </button>
  );
}
